import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getExitRequests, submitExitRequest, updateExitRequest, cancelExitRequest, getEmployees, getErrorMessage } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { LoadingOverlay } from "../components/LoadingOverlay";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending:   { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  approved:  { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  rejected:  { bg: "#fef2f2", text: "#991b1b", border: "#fca5a5" },
  completed: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
};

// Confirmation copy per status transition — named actions, not the raw enum.
const STATUS_CONFIRM: Record<string, { title: string; message: (name: string) => string; confirmLabel: string; danger: boolean }> = {
  approved:  { title: "Approve exit",  message: n => `Approve ${n}'s exit request?`, confirmLabel: "Approve", danger: false },
  rejected:  { title: "Reject exit",   message: n => `Reject ${n}'s exit request?`,  confirmLabel: "Reject",  danger: true  },
  completed: { title: "Complete exit", message: n => `Mark ${n}'s offboarding as completed? Unsaved clearance notes are saved with it.`, confirmLabel: "Mark Completed", danger: false },
};

interface ExitRequest {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_email: string;
  designation: string;
  department: string;
  resignation_date: string | null;
  last_working_date: string | null;
  reason: string | null;
  status: string;
  clearance_items: { item: string; completed: boolean; notes: string }[];
  clearance_done: number;
  clearance_total: number;
  final_settlement_amount: number | null;
  final_settlement_notes: string | null;
  hr_notes: string | null;
  exit_type: string | null;
  is_regretted: boolean | null;
  approver_name: string | null;
  approved_at: string | null;
  created_at: string;
}

function ClearanceChecklist({ items, onChange, editable }: {
  items: { item: string; completed: boolean; notes: string }[];
  onChange: (items: any[]) => void;
  editable: boolean;
}) {
  const done = items.filter(i => i.completed).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? TEAL : NAVY, borderRadius: 4, transition: "width 0.3s" }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: pct === 100 ? "#166534" : NAVY }}>
          {done}/{items.length}
        </span>
      </div>
      {items.map((item, i) => (
        <div key={i} style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          padding: "10px 12px", borderRadius: 8, marginBottom: 6,
          background: item.completed ? "#f0fdf4" : "#f8fafc",
          border: `1px solid ${item.completed ? "#86efac" : "#e2e8f0"}`,
        }}>
          <input
            type="checkbox"
            checked={item.completed}
            disabled={!editable}
            onChange={e => {
              const updated = items.map((it, j) => j === i ? { ...it, completed: e.target.checked } : it);
              onChange(updated);
            }}
            style={{ marginTop: 2, cursor: editable ? "pointer" : "default", accentColor: NAVY }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: item.completed ? "#166534" : "#1e293b", fontWeight: 500,
              textDecoration: item.completed ? "line-through" : "none" }}>
              {item.item}
            </div>
            {editable && (
              <input
                value={item.notes}
                onChange={e => {
                  const updated = items.map((it, j) => j === i ? { ...it, notes: e.target.value } : it);
                  onChange(updated);
                }}
                placeholder="Add notes..."
                style={{ marginTop: 4, width: "100%", fontSize: 12, color: "#64748b",
                  border: "none", background: "transparent", outline: "none", padding: 0 }}
              />
            )}
            {!editable && item.notes && (
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{item.notes}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ExitDetail({ exit, onClose, isHR }: { exit: ExitRequest; onClose: () => void; isHR: boolean }) {
  const tz = useTenantTimezone();
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [clearance, setClearance] = useState(exit.clearance_items);
  const [settlement, setSettlement] = useState(exit.final_settlement_amount?.toString() || "");
  const [settlementNotes, setSettlementNotes] = useState(exit.final_settlement_notes || "");
  const [hrNotes, setHrNotes] = useState(exit.hr_notes || "");
  // Exit classification feeds the Attrition report. Left blank it stays
  // unrecorded and reports as "Not recorded" — never guessed into a category.
  const [exitType, setExitType] = useState(exit.exit_type || "");
  const [regretted, setRegretted] = useState<string>(
    exit.is_regretted === null || exit.is_regretted === undefined ? "" : String(exit.is_regretted));
  const [saving, setSaving] = useState(false);

  const updateMut = useMutation({
    mutationFn: (data: any) => updateExitRequest(exit.id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exits"] }),
  });

  async function saveProgress() {
    setSaving(true);
    try {
      await updateMut.mutateAsync({
        clearance_items: clearance,
        final_settlement_amount: settlement ? parseFloat(settlement) : null,
        final_settlement_notes: settlementNotes,
        hr_notes: hrNotes,
        exit_type: exitType,
        // "" means "not recorded" and must stay null, so only a real choice is sent.
        ...(regretted === "" ? {} : { is_regretted: regretted === "true" }),
      });
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(status: string) {
    const c = STATUS_CONFIRM[status];
    const ok = c
      ? await confirm(c.message(exit.employee_name), { title: c.title, confirmLabel: c.confirmLabel, danger: c.danger })
      : await confirm(`Mark ${exit.employee_name}'s exit as ${status}?`, { title: "Update exit" });
    if (!ok) return;
    try {
      // Completing an exit also persists the checklist so unsaved ticks aren't lost (C4-52).
      await updateMut.mutateAsync(status === "completed" ? { status, clearance_items: clearance } : { status });
    } catch (e) {
      toast(getErrorMessage(e, `Failed to mark exit as ${status}`), "error");
      return;
    }
    onClose();
  }

  const clearanceDone = clearance.filter(i => i.completed).length;
  const allCleared = clearanceDone === clearance.length;

  const sc = STATUS_COLORS[exit.status] || STATUS_COLORS.pending;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <LoadingOverlay active={updateMut.isPending} />
      <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a" }}>{exit.employee_name}</h2>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 2 }}>{exit.designation} {exit.department ? `· ${exit.department}` : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 12, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>
              {exit.status.toUpperCase()}
            </span>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
          </div>
        </div>

        {/* Details */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
          {[
            ["Resignation date", formatDate(exit.resignation_date)],
            ["Last working day", formatDate(exit.last_working_date)],
            ["Submitted", formatDate(exit.created_at, "short", tz)],
            ["Approved by", exit.approver_name || "—"],
          ].map(([label, value]) => (
            <div key={label} style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
              <div style={{ fontSize: 14, color: "#1e293b", fontWeight: 600, marginTop: 2 }}>{value}</div>
            </div>
          ))}
        </div>

        {exit.reason && (
          <div style={{ background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 24 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>Reason</div>
            <div style={{ fontSize: 13, color: "#475569" }}>{exit.reason}</div>
          </div>
        )}

        {/* Clearance */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Clearance Checklist</h3>
          <ClearanceChecklist items={clearance} onChange={setClearance} editable={isHR} />
        </div>

        {/* Final Settlement (HR only) */}
        {isHR && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Final Settlement</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Amount</label>
                <input
                  type="number"
                  value={settlement}
                  onChange={e => setSettlement(e.target.value)}
                  placeholder="0.00"
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
                <input
                  value={settlementNotes}
                  onChange={e => setSettlementNotes(e.target.value)}
                  placeholder="Breakdown of settlement..."
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" }}
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Exit Type</label>
                <select
                  value={exitType}
                  onChange={e => setExitType(e.target.value)}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box", minHeight: 44 }}
                >
                  <option value="">Not recorded</option>
                  <option value="voluntary">Voluntary</option>
                  <option value="involuntary">Involuntary</option>
                  <option value="end_of_contract">End of contract</option>
                  <option value="retirement">Retirement</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Regretted?</label>
                <select
                  value={regretted}
                  onChange={e => setRegretted(e.target.value)}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box", minHeight: 44 }}
                >
                  <option value="">Not recorded</option>
                  <option value="true">Yes — we did not want to lose them</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>HR Notes</label>
              <textarea
                value={hrNotes}
                onChange={e => setHrNotes(e.target.value)}
                placeholder="Internal notes..."
                rows={2}
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {isHR && (
            <button onClick={saveProgress} disabled={saving} style={{
              padding: "9px 20px", background: NAVY, color: "white", border: "none",
              borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
            }}>
              {saving ? "Saving…" : "Save Progress"}
            </button>
          )}
          {isHR && exit.status === "pending" && (
            <>
              <AsyncButton onClick={() => changeStatus("approved")} style={{
                padding: "9px 20px", background: "#f0fdf4", color: "#166534",
                border: "1.5px solid #86efac", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
              }}>Approve</AsyncButton>
              <AsyncButton onClick={() => changeStatus("rejected")} style={{
                padding: "9px 20px", background: "#fef2f2", color: "#991b1b",
                border: "1.5px solid #fca5a5", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
              }}>Reject</AsyncButton>
            </>
          )}
          {isHR && exit.status === "approved" && (
            <>
              <AsyncButton
                onClick={() => changeStatus("completed")}
                disabled={!allCleared}
                title={allCleared ? undefined : `Complete all clearance items first (${clearanceDone}/${clearance.length} done)`}
                style={{
                  padding: "9px 20px", background: "#eff6ff", color: "#1d4ed8",
                  border: "1.5px solid #bfdbfe", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
                }}
              >Mark Completed</AsyncButton>
              {!allCleared && (
                <span style={{ alignSelf: "center", fontSize: 12, color: "#94a3b8" }}>
                  Clearance {clearanceDone}/{clearance.length} — complete all items to finish offboarding
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ExitPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const isHR = user?.role === "admin" || user?.role === "admin_assistant";
  const [selected, setSelected] = useState<ExitRequest | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ resignation_date: "", last_working_date: "", reason: "", employee_id: "" });

  const { data: exits = [], isLoading } = useQuery({ queryKey: ["exits"], queryFn: getExitRequests });
  const { data: employeesData = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees, enabled: isHR });
  const all = exits as ExitRequest[];
  const employees = employeesData as any[];

  const submitMut = useMutation({
    mutationFn: (payload: any) => submitExitRequest(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exits"] }); setShowForm(false); setForm({ resignation_date: "", last_working_date: "", reason: "", employee_id: "" }); },
    onError: (e: any) => toast(e.response?.data?.detail || "Failed to submit", "error"),
  });

  const cancelMut = useMutation({
    mutationFn: cancelExitRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exits"] }),
  });

  const myRequest = all.find(e => e.employee_id === user?.id);
  const pendingCount = all.filter(e => e.status === "pending").length;

  return (
    <div>
      <LoadingOverlay active={submitMut.isPending || cancelMut.isPending} />
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>Offboarding</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
            {isHR ? `${all.length} total · ${pendingCount} pending` : "Manage your resignation and offboarding"}
          </p>
        </div>
        {(!isHR && !myRequest) || isHR ? (
          <button onClick={() => { if (isHR) setForm(f => ({ ...f, employee_id: String(user?.id || "") })); setShowForm(true); }} style={{
            padding: "10px 20px", background: "#dc2626", color: "white", border: "none",
            borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14,
          }}>{isHR ? "+ Log Resignation" : "Submit Resignation"}</button>
        ) : null}
      </div>

      {/* Submit / log form */}
      {showForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700 }}>{isHR ? "Log Resignation" : "Submit Resignation"}</h3>
          {isHR && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Employee *</label>
              <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }}>
                <option value="">— Select employee —</option>
                <option value={String(user?.id)}>Myself ({user?.name || user?.email})</option>
                {employees.filter((e: any) => e.id !== user?.id).map((e: any) => <option key={e.id} value={e.id}>{e.name} ({e.email})</option>)}
              </select>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Resignation Date</label>
              <DateInput value={form.resignation_date} onChange={e => setForm(f => ({ ...f, resignation_date: e.target.value }))}
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Last Working Day</label>
              <DateInput value={form.last_working_date} onChange={e => setForm(f => ({ ...f, last_working_date: e.target.value }))}
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>Reason (optional)</label>
            <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              rows={3} placeholder="Please share your reason for leaving..."
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => {
              const payload: any = { resignation_date: form.resignation_date, last_working_date: form.last_working_date, reason: form.reason };
              if (isHR && form.employee_id) payload.employee_id = parseInt(form.employee_id);
              submitMut.mutate(payload);
            }} disabled={submitMut.isPending || (isHR && !form.employee_id)} style={{
              padding: "9px 20px", background: "#dc2626", color: "white", border: "none",
              borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
              opacity: (isHR && !form.employee_id) ? 0.5 : 1,
            }}>{submitMut.isPending ? "Submitting…" : isHR ? "Log Resignation" : "Submit Resignation"}</button>
            <button onClick={() => setShowForm(false)} style={{
              padding: "9px 20px", background: "white", color: "#64748b", border: "1.5px solid #e2e8f0",
              borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13,
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>Loading…</div>
      ) : all.length === 0 ? (
        <div style={{ textAlign: "center", padding: 80, color: "#94a3b8" }}>
          <div style={{ fontSize: 48 }}>🚪</div>
          <div style={{ marginTop: 12 }}>No exit requests yet.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {all.map(exit => {
            const sc = STATUS_COLORS[exit.status] || STATUS_COLORS.pending;
            const pct = exit.clearance_total ? Math.round((exit.clearance_done / exit.clearance_total) * 100) : 0;
            return (
              <div key={exit.id} onClick={() => setSelected(exit)} style={{
                background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
                padding: "16px 20px", cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                transition: "border-color 0.15s",
              }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = NAVY)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = "#e2e8f0")}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{exit.employee_name}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                      {exit.designation}{exit.department ? ` · ${exit.department}` : ""}{exit.last_working_date ? ` · Last day: ${formatDate(exit.last_working_date)}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 10, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>
                      {exit.status.toUpperCase()}
                    </span>
                    {exit.employee_id === user?.id && exit.status === "pending" && (
                      <button onClick={async e => {
                        e.stopPropagation();
                        if (await confirm("Withdraw your resignation? The request will be marked cancelled.", { title: "Cancel resignation", confirmLabel: "Withdraw" })) cancelMut.mutate(exit.id);
                      }}
                        disabled={cancelMut.isPending}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 13, padding: "2px 6px" }}>
                        ✕ Cancel
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? TEAL : NAVY, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 11, color: "#64748b" }}>Clearance {exit.clearance_done}/{exit.clearance_total}</span>
                  {exit.final_settlement_amount != null && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#166534" }}>Settlement: {exit.final_settlement_amount.toLocaleString()}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <ExitDetail exit={selected} onClose={() => setSelected(null)} isHR={isHR} />
      )}
    </div>
  );
}
